import { PageHead } from "@generalapplications/html-render/react";
import { Grid, PageArticle, Stack } from "@generalapplications/ui";
import { useState } from "react";
import { BlogEmptyState } from "@/app/pages/blog/components/BlogEmptyState";
import {
  ArticleCard,
  FeaturedArticleCard,
} from "@/app/features/blog/components/BlogPostCard";
import { BlogRssLink } from "@/app/pages/blog/components/BlogRssLink";
import { BlogTagFilter } from "@/app/pages/blog/components/BlogTagFilter";
import { PageHero } from "@/app/common/components/composite/layout/PageHero";
import { Reveal } from "@/app/common/components/composite/motion/Reveal";
import {
  RevealGroup,
  RevealItem,
} from "@/app/common/components/composite/motion/RevealGroup";
import {
  blogIndexPage,
  blogPosts,
  isPreviewableBlogPost,
  sortBlogPosts,
} from "@/content/blog";
import { pageHead } from "@/app/common/functions/seo/pageHead";
import { routes } from "@/app/common/functions/routes/routes";

export function BlogPage() {
  const head = pageHead({
    path: routes.blog.index,
    title: blogIndexPage.seoTitle,
    description: blogIndexPage.seoDescription,
    imagePath: blogIndexPage.heroImage,
  });

  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const visiblePosts = sortBlogPosts(blogPosts.filter(isPreviewableBlogPost));
  const allTags = Array.from(
    new Set(visiblePosts.flatMap((post) => post.tags)),
  ).sort();
  const filteredPosts = selectedTag
    ? visiblePosts.filter((post) => post.tags.includes(selectedTag))
    : visiblePosts;
  const [featuredPost, ...remainingPosts] = filteredPosts;

  return (
    <PageArticle rhythm="xl">
      <PageHead head={head} />
      <PageHero
        headline={blogIndexPage.headline}
        copy={blogIndexPage.heroCopy}
        image={blogIndexPage.heroImage}
      >
        <Stack gap="md" align="start">
          <BlogRssLink />
          <BlogTagFilter
            tags={allTags}
            selectedTag={selectedTag}
            onSelectedTagChange={setSelectedTag}
          />
        </Stack>
      </PageHero>

      {filteredPosts.length === 0 ? (
        <Reveal>
          <BlogEmptyState selectedTag={selectedTag} />
        </Reveal>
      ) : (
        <Stack as="section" aria-label="Articles" gap="xl">
          {featuredPost && (
            <Reveal>
              <FeaturedArticleCard post={featuredPost} />
            </Reveal>
          )}
          {remainingPosts.length > 0 && (
            <RevealGroup>
              <Grid columns={{ base: 1, md: 2 }} gap="lg">
                {remainingPosts.map((post) => (
                  <RevealItem key={post.slug}>
                    <ArticleCard post={post} />
                  </RevealItem>
                ))}
              </Grid>
            </RevealGroup>
          )}
        </Stack>
      )}
    </PageArticle>
  );
}
